import { EXCEPTION_KIND_LABEL, type ExceptionKind } from '@serievo/contracts';
import { Alert, Button, Card, Col, List, Row, Skeleton, Tag, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router';
import { formatInt, formatMoney } from '../api/client';
import { useOverview } from '../api/reports';
import { DailyBars, Kpi, periodLabel } from '../components/ui';
import { useWork } from '../features/context/WorkContext';
import { palette } from '../theme';

/** Tổng quan kỳ: KPI, đơn theo ngày, ngoại lệ đang mở. Số liệu đọc từ API. */
export function OverviewPage() {
  const w = useWork();
  const q = useOverview(w);
  const nav = useNavigate();
  const loc = useLocation();
  const d = q.data;

  if (q.error) return <Alert style={{ margin: 16 }} type="error" showIcon message={q.error.message} />;
  if (!d) return <Skeleton active style={{ padding: 16 }} />;

  return (
    <div className="page">
      <Typography.Title level={4} className="page-title">
        Tổng quan · {periodLabel(d.period)}
        {w.shop && <Tag style={{ marginLeft: 8 }}>{w.shop.shopName}</Tag>}
      </Typography.Title>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi
            k="revenue"
            title="Doanh thu chưa VAT"
            value={formatMoney(d.revenue.preTax)}
            suffix="₫"
            hint={`${formatInt(d.invoices.original)} hóa đơn gốc, ${formatInt(d.invoices.adjustment)} điều chỉnh`}
          />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="tax" title="Thuế GTGT đầu ra" value={formatMoney(d.revenue.vatOut)} suffix="₫" hint="Thuế suất 8%" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi
            k="source"
            title="Đơn giao thành công"
            value={formatInt(d.orders.delivered)}
            hint={`${formatInt(d.orders.returned)} đơn trả · ${formatInt(d.orders.cancelled)} đơn hủy`}
          />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="fee" title="Phí sàn" value={formatMoney(d.fees)} suffix="₫" hint="Theo ngày quyết toán" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi k="matched" title="Tiền về tài khoản" value={formatMoney(d.payouts)} suffix="₫" hint="Theo ngày sàn chuyển" />
        </Col>
        <Col xs={24} sm={12} xl={8} xxl={4}>
          <Kpi
            k="time"
            title="Ngoại lệ đang mở"
            value={formatInt(d.exceptions.open)}
            hint={`${formatInt(d.exceptions.blocking)} ngoại lệ chặn khóa kỳ`}
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Đơn giao thành công theo ngày">
            <DailyBars
              color={palette.blue}
              data={d.daily.map((x) => ({
                date: x.date,
                value: x.orders,
                label: `${x.date.split('-').reverse().join('/')}: ${formatInt(x.orders)} đơn · ${formatMoney(x.gross)} ₫`,
              }))}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            size="small"
            title="Ngoại lệ đối soát R1 đang mở"
            extra={
              <Button size="small" type="link" onClick={() => nav({ pathname: '/doi-soat', search: loc.search })}>
                Xem đối soát
              </Button>
            }
          >
            <List
              size="small"
              dataSource={Object.entries(d.exceptions.byKind).sort((a, b) => b[1] - a[1])}
              locale={{ emptyText: 'Không có ngoại lệ đang mở' }}
              renderItem={([kind, n]) => (
                <List.Item>
                  <span>{EXCEPTION_KIND_LABEL[kind as ExceptionKind] ?? kind}</span>
                  <Typography.Text strong className="num">
                    {formatInt(n)}
                  </Typography.Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
